import React, { useEffect, useState } from 'react';
import { Button, Flex, Popconfirm, Spin, Drawer, DatePicker, Select, Modal, Space } from 'antd';
import Table, { MAX_PAGE_SIZE } from 'components/Common/Table';
import { UserAddOutlined, EditOutlined, DeleteOutlined, MoreOutlined, CopyOutlined, DownloadOutlined, PrinterOutlined, SendOutlined, EyeOutlined, FileDoneOutlined } from '@ant-design/icons';
import { useNavigate } from "react-router-dom"
import * as actions from "store/actions/AuthActions"
import dayjs from 'dayjs';
import { message } from 'antd/es';
import { Dropdown } from 'antd/lib';
import axios from 'axios';
import fileDownload from 'js-file-download';
import * as adminactions from "store/actions/AuthActions";
import PreviewDrawerActions from '../Common/PreviewDrawerActions';
import SendQuoteDrawer from '../../pages/Quotes/SendQuoteDrawer';
import PdfViewer from '../Common/PdfViewer';
import FilterBar, { FilterField } from '../Common/FilterBar';
import useUrlFilters from 'hooks/useUrlFilters';
import { formatMoney, recordCurrency } from 'utils/money';

const getStatusStyle = (status, expiryDate) => {
  const statusLower = status?.toLowerCase();
  const isExpired = expiryDate && dayjs(expiryDate).isBefore(dayjs(), 'day');

  // PARTIAL: red if expired, orange otherwise
  if (statusLower === 'partial') {
    const color = isExpired ? "#DC2626" : "#F97316";
    return { backgroundColor: `${color}20`, color: color };
  }

  const colors = {
    paid: "#16A34A",      // green
    sent: "#F97316",      // orange
    draft: "#DC2626",     // red
    ready: "#16A34A",     // green
  };

  const color = colors[statusLower] || "#6B7280";
  return { backgroundColor: `${color}20`, color: color };
};

// Reusable menu-item label: an icon + text row that stops row-click propagation
const menuItem = (icon, text, onClick, extraStyle = {}) => ({
  label: (
    <span
      onClick={(e) => { e.stopPropagation(); onClick(e); }}
      style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', ...extraStyle }}
    >
      <span style={{ marginRight: '8px', display: 'inline-flex' }}>{icon}</span>
      {text}
    </span>
  ),
  onClick: (e) => {
    e.domEvent.stopPropagation();
  },
});

const getItemsForDropDown = (record, handlers) => {
  const {
    handleDelete,
    handleViewQuote,
    handlePrintQuote,
    handleDownloadQuote,
    handleSendQuote,
    handleConvertToInvoice,
    handleCloneQuote,
  } = handlers;

  const items = [
    {
      key: 'edit',
      label: (
        <a href={`/quotes/edit/${record.id}/`} target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center' }} onClick={(e) => e.stopPropagation()}>
          <EditOutlined style={{ marginRight: '8px' }} />
          Edit
        </a>
      ),
      onClick: (e) => {
        e.domEvent.stopPropagation();
      }
    },
    { ...menuItem(<EyeOutlined />, 'View Quote', () => handleViewQuote(record)), key: 'view' },
  ];

  // PDF-based actions are only meaningful for auto-generated quotes,
  // matching the buttons on the quote edit page.
  if (!record.is_manual) {
    items.push({ ...menuItem(<PrinterOutlined />, 'Print Quote', () => handlePrintQuote(record)), key: 'print' });
    items.push({ ...menuItem(<DownloadOutlined />, 'Download Quote', () => handleDownloadQuote(record)), key: 'download' });
  }

  items.push({ ...menuItem(<SendOutlined />, 'Send Quote', () => handleSendQuote(record)), key: 'send' });

  if (!record.is_manual) {
    items.push({ type: 'divider', key: 'div-create' });
    items.push({ ...menuItem(<FileDoneOutlined />, 'Convert to Invoice', () => handleConvertToInvoice(record)), key: 'convert' });
    items.push({ ...menuItem(<CopyOutlined />, 'Clone Quote', () => handleCloneQuote(record)), key: 'clone' });
  }

  items.push({ type: 'divider', key: 'div-misc' });

  items.push({
    key: 'delete',
    label: (<Popconfirm title="Sure to delete?"
      onConfirm={() => handleDelete(record.id)}
      onClick={(e) => e.stopPropagation()}
    >
      <a style={{ display: 'flex', alignItems: 'center', color: '#DC2626' }} onClick={(e) => e.stopPropagation()}>
        <DeleteOutlined style={{ marginRight: '8px' }} />
        Delete
      </a>
    </Popconfirm>),
    onClick: (e) => {
      e.domEvent.stopPropagation();
    }
  });

  return items;
}

// Every filter, the search and the page live in the URL so a filtered list can
// be shared as a link. Param names match the old hand-rolled sync.
const URL_FILTERS = {
  search: { type: 'string' },
  page: { type: 'number', default: 1 },
  pageSize: { type: 'number', key: 'page_size', default: 100 },
  dateRange: { type: 'dateRange', keys: ['start_date', 'end_date'] },
  expiryDateRange: { type: 'dateRange', keys: ['expiry_start_date', 'expiry_end_date'] },
  account: { type: 'string' },
  status: { type: 'array' },
  products: { type: 'array', key: 'product' },
};

const QuotesTable = () => {

  const [messageApi, contextHolder] = message.useMessage();
  const [modal, modalContextHolder] = Modal.useModal();
  const [sendDrawerOpen, setSendDrawerOpen] = useState(false);
  const [quoteForSend, setQuoteForSend] = useState(null);
  const navigate = useNavigate();
  const [filters, setFilters] = useUrlFilters(URL_FILTERS);
  const urlSetter = (name) => (value) => setFilters({ [name]: value });

  const searchTerm = filters.search || "";
  const setSearchTerm = urlSetter('search');
  const [searchTermLive, setSearchTermLive] = useState(searchTerm);
  // Keep the box in step when the URL changes underneath it (back/forward).
  useEffect(() => setSearchTermLive(searchTerm), [searchTerm]);

  const dateRange = filters.dateRange;
  const expiryDateRange = filters.expiryDateRange;
  const selectedAccount = filters.account;
  // Status and product are multi-select: a list matches quotes in any of them.
  const selectedStatus = filters.status;
  const selectedProducts = filters.products;
  const [accounts, setAccounts] = useState([]);
  const [products, setProducts] = useState([]);

  // Backend pagination state
  const currentPage = filters.page;
  const setCurrentPage = urlSetter('page');
  const pageSize = Math.min(filters.pageSize, MAX_PAGE_SIZE);
  const setPageSize = urlSetter('pageSize');
  const [totalRows, setTotalRows] = useState(0);

  const [dataSource, setDataSource] = useState([]);
  const [loading, setLoading] = useState(false);

  const [quoteStatuses, setQuoteStatuses] = useState([]);

  // State for drawer
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [pdfUrl, setPdfUrl] = useState("");
  const [pdfLoading, setPdfLoading] = useState(false);
  const [, setPreviewPdfVisible] = useState(false);
  const [, setPdfPreviewData] = useState(null);

  const hasRange = (range) => Boolean(range && range[0] && range[1]);

  const fetchQuotes = async (page, size) => {
    setLoading(true);
    try {
      const response = await actions.loadQuotes(page, searchTerm, null, dateRange, selectedAccount, selectedStatus, size, {
        products: selectedProducts,
        expiryDateRange,
      });
      if (response) {
        setDataSource(response.results || []);
        setTotalRows(response.count || 0);
      }
    } catch (e) {
      console.error('Error fetching quotes:', e);
    }
    setLoading(false);
  }

  const onSearch = (value) => {
    if (searchTerm === value) {
      return;
    } else {
      setSearchTerm(value);
      setCurrentPage(1); // Reset to first page on search
    }
  }

  const loadQuoteStatuses = async () => {
    const response = await actions.loadQuoteStatuses();
    if (response) {
      setQuoteStatuses(response);
    }
  }

  const loadAccounts = async () => {
    try {
      const response = await actions.loadAccounts(1, "", 1000);
      if (response?.status === 200) {
        setAccounts(response.data.results || []);
      }
    } catch (e) {
      console.log("Error loading accounts:", e);
    }
  }

  const loadProducts = async () => {
    try {
      const response = await actions.loadPaymentProducts();
      if (response?.status === 200) {
        setProducts(response.data.results || response.data || []);
      }
    } catch (e) {
      console.log("Error loading products:", e);
    }
  }

  // Every filter change goes back to page 1.
  const onFilterChange = (key) => (value) => setFilters({ [key]: value, page: 1 });

  const onDateRangeChange = onFilterChange('dateRange');
  const onExpiryDateRangeChange = onFilterChange('expiryDateRange');
  const onAccountChange = onFilterChange('account');
  const onStatusChange = (value) => onFilterChange('status')(value || []);
  const onProductsChange = (value) => onFilterChange('products')(value || []);

  // Search lives outside the filter panel, so it is neither counted nor cleared.
  const activeFilterCount =
    (hasRange(dateRange) ? 1 : 0) +
    (hasRange(expiryDateRange) ? 1 : 0) +
    (selectedAccount ? 1 : 0) +
    selectedStatus.length +
    selectedProducts.length;

  const clearFilters = () => {
    setFilters({ dateRange: null, expiryDateRange: null, account: null, status: [], products: [], page: 1 });
  }

  useEffect(() => {
    loadQuoteStatuses();
    loadAccounts();
    loadProducts();
  }, []);

  // Fetch quotes when filters or pagination change
  useEffect(() => {
    fetchQuotes(currentPage, pageSize);
  }, [searchTerm, dateRange, expiryDateRange, selectedAccount, selectedStatus, selectedProducts, currentPage, pageSize]);

  const handleDelete = async (id) => {
    const newData = dataSource.find((item) => item.id === id);
    const response = await actions.deleteQuotes(newData.id);
    console.log(response);
    if (response.status === 204) {
      // Refresh the data after deletion
      fetchQuotes(currentPage, pageSize);
    }
  };

  const rowSelection = {
    onChange: (selectedRowKeys, selectedRows) => {
      console.log(`selectedRowKeys: ${selectedRowKeys}`, 'selectedRows: ', selectedRows);
    },
  }

  const handleRowClick = async (record) => {
    setSelectedQuote(record);
    setDrawerVisible(true);
    setPdfLoading(true);
    try {
      const response = await axios.get(
        `quotes/quotes/${record.id}/quoteView/`,
        { responseType: 'blob' }
      );
      const blobUrl = URL.createObjectURL(response.data);
      setPdfUrl(blobUrl);
    } catch (error) {
      console.error('Error loading quote PDF:', error);
      messageApi.error('Failed to load quote preview');
    } finally {
      setPdfLoading(false);
    }
  };

  const handleDrawerClose = () => {
    setDrawerVisible(false);
    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
      setPdfUrl("");
    }
  };

  const previewDownloadPdf = async (preview, download) => {
    const response = await adminactions.downloadQuote(
      selectedQuote?.id,
      true,
      "QUOTE_TEMPLATE"
    );
    console.log({ response });
    if (response) {
      if (download) {
        const filename = selectedQuote?.qt_number ? `${selectedQuote.qt_number}.pdf` : "quote.pdf";
        fileDownload(response, filename);
      }
      if (preview) {
        setPdfPreviewData(response);
        setPreviewPdfVisible(true);
      }
    }
  };

  // View / Print both open the quote-details drawer (which has a Print button),
  // mirroring the invoice list.
  const handleViewQuote = (record) => {
    handleRowClick(record);
  };

  const handlePrintQuote = (record) => {
    handleRowClick(record);
  };

  const handleDownloadQuote = async (record) => {
    messageApi.open({ type: 'loading', content: 'Preparing download...', key: 'qt-dl' });
    try {
      const response = await adminactions.downloadQuote(record.id, true, "QUOTE_TEMPLATE");
      if (response) {
        const filename = record?.qt_number ? `${record.qt_number}.pdf` : "quote.pdf";
        fileDownload(response, filename);
        messageApi.open({ type: 'success', content: 'Quote downloaded.', key: 'qt-dl' });
      } else {
        messageApi.open({ type: 'error', content: 'Failed to download quote.', key: 'qt-dl' });
      }
    } catch (e) {
      messageApi.open({ type: 'error', content: 'Failed to download quote.', key: 'qt-dl' });
    }
  };

  // The send drawer needs the full quote (attachments, account email), which
  // the list rows don't carry.
  const handleSendQuote = async (record) => {
    setSendDrawerOpen(true);
    setQuoteForSend(null);
    const quote = await adminactions.loadQuote(record.id);
    if (quote) {
      setQuoteForSend(quote);
    } else {
      setSendDrawerOpen(false);
      messageApi.error('Failed to load quote');
    }
  };

  const handleConvertToInvoice = (record) => {
    modal.confirm({
      title: `Convert ${record.qt_number || 'this quote'} to an invoice?`,
      content: 'A new invoice is created from this quote, and you are taken to it.',
      okText: 'Convert',
      onOk: async () => {
        const response = await adminactions.convertQuoteToInvoice(record.id, {});
        if (response) {
          messageApi.success(`Quote converted to Invoice ${response.inv_number}`);
          navigate(`/invoices/edit/${response.id}`);
        } else {
          messageApi.error('Failed to convert quote to invoice');
        }
      },
    });
  };

  const handleCloneQuote = async (record) => {
    messageApi.open({ type: 'loading', content: 'Cloning quote...', key: 'qt-clone' });
    const response = await adminactions.cloneQuote(record.id);
    if (response) {
      messageApi.open({ type: 'success', content: `Quote cloned as ${response.qt_number || 'a new quote'}.`, key: 'qt-clone' });
      navigate(`/quotes/edit/${response.id}`);
    } else {
      messageApi.open({ type: 'error', content: 'Failed to clone quote.', key: 'qt-clone' });
    }
  };

  const defaultColumns = [
    {
      title: 'STATUS',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      filters: quoteStatuses.map((is, i) => {
        return {
          text: is[0],
          value: is[0]
        }
      }),
      sorter: (a, b) => a?.status.localeCompare(b?.status),
      onFilter: (value, record) => record.status === value,
      render: (status, record) => (
        <span style={{
          ...getStatusStyle(status, record.expiry_date),
          padding: "4px 12px",
          borderRadius: "9999px",
          fontSize: "12px",
          fontWeight: 500,
        }}>
          {status}
        </span>
      ),
    },
    {
      title: 'NUMBER',
      dataIndex: 'qt_number',
      key: 'qt_number',
      sorter: (a, b) => a?.qt_number.localeCompare(b?.qt_number),
      width: 220,
      render: (qt_number, record) => (
        <a
          href={`/quotes/edit/${record.id}/`}
          onClick={(e) => {
            e.stopPropagation();
          }}
          style={{
            color: '#1890ff',
            textDecoration: 'none',
            cursor: 'pointer',
          }}
          onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
          onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
        >
          {qt_number}
        </a>
      ),
    },
    {
      title: 'TITLE',
      dataIndex: 'title',
      key: 'title',
      sorter: (a, b) => (a?.title || '').localeCompare(b?.title || ''),
      width: 220,
      ellipsis: true,
    },
    {
      title: 'ACCOUNT',
      dataIndex: ["account_data", "name"],
      key: 'account_name',
      sorter: (a, b) => a?.account_name.localeCompare(b?.account_name),
      width: 100,
      ellipsis: true,
    },
    {
      title: 'AMOUNT',
      dataIndex: 'total_payable',
      key: 'total_payable',
      sorter: (a, b) => a?.total_payable - b?.total_payable,
      width: 120,
      align: 'right',
      render: (value, record) => (
        <span>{formatMoney(value, recordCurrency(record))}</span>
      ),
    },
    {
      title: 'DATE',
      dataIndex: 'quote_date',
      key: 'quote_date',
      align: 'right',
      width: 120,
      sorter: (a, b) => dayjs(a?.quote_date) - dayjs(b?.quote_date),
      render: (quote_date) => (
        <span style={{ whiteSpace: 'nowrap' }}>
          <span style={{ fontSize: '9px', color: '#888', marginRight: '4px' }}>{dayjs(quote_date).format("ddd")}</span>
          <span>{dayjs(quote_date).format("YYYY-MM-DD")}</span>
        </span>
      ),
    },
    {
      title: 'EXPIRY DATE',
      dataIndex: 'expiry_date',
      key: 'expiry_date',
      align: 'right',
      width: 120,
      sorter: (a, b) => dayjs(a?.expiry_date) - dayjs(b?.expiry_date),
      render: (expiry_date) => (
        <span style={{ whiteSpace: 'nowrap' }}>
          <span style={{ fontSize: '9px', color: '#888', marginRight: '4px' }}>{dayjs(expiry_date).format("ddd")}</span>
          <span>{dayjs(expiry_date).format("YYYY-MM-DD")}</span>
        </span>
      ),
    },
    {
      title: 'URGENCY STATUS',
      dataIndex: 'expiry_date',
      key: 'urgency_status',
      align: 'center',
      width: 180,
      sorter: (a, b) => {
        const getUrgencyLevel = (record) => {
          const statusLower = record.status?.toLowerCase();
          if (statusLower === 'accepted' || statusLower === 'converted') return 0;
          const today = dayjs();
          const expiryDate = dayjs(record.expiry_date);
          const daysUntilExpiry = expiryDate.diff(today, 'days');
          if (daysUntilExpiry < -30) return 4; // Critical
          if (daysUntilExpiry < 0) return 3;   // Expired
          if (daysUntilExpiry <= 7) return 2;  // Expiring Soon
          return 1;                             // On Time
        };
        return getUrgencyLevel(a) - getUrgencyLevel(b);
      },
      render: (expiry_date, record) => {
        const statusLower = record.status?.toLowerCase();
        if (statusLower === 'accepted' || statusLower === 'converted') {
          return (
            <span style={{
              backgroundColor: '#16A34A20',
              color: '#16A34A',
              padding: '4px 12px',
              borderRadius: '9999px',
              fontSize: '12px',
              fontWeight: 500,
              whiteSpace: 'nowrap',
            }}>
              Accepted
            </span>
          );
        }

        const today = dayjs();
        const expiryDateMoment = dayjs(expiry_date);
        const daysUntilExpiry = expiryDateMoment.diff(today, 'days');
        const expiredDays = Math.abs(daysUntilExpiry);

        let label, color;
        if (daysUntilExpiry < -30) {
          label = `Critical (${expiredDays} days)`;
          color = '#991B1B'; // Dark red
        } else if (daysUntilExpiry < 0) {
          label = `Expired (${expiredDays} days)`;
          color = '#DC2626'; // Red
        } else if (daysUntilExpiry <= 7) {
          label = daysUntilExpiry === 0 ? 'Expires Today' : `Expiring Soon (${daysUntilExpiry} days)`;
          color = '#F59E0B'; // Amber/Yellow
        } else {
          label = 'On Time';
          color = '#16A34A'; // Green
        }

        return (
          <span style={{
            backgroundColor: `${color}20`,
            color: color,
            padding: '4px 12px',
            borderRadius: '9999px',
            fontSize: '12px',
            fontWeight: 500,
            whiteSpace: 'nowrap',
          }}>
            {label}
          </span>
        );
      }
    },
    {
      title: 'Actions',
      dataIndex: 'edit',
      key: 'edit',
      width: 220,
      render: (_, record) => {
        return dataSource?.length >= 1 ? (
          <Flex gap="middle" style={{ padding: 5 }}>
            <Dropdown
              menu={{ items: getItemsForDropDown(record, {
                handleDelete,
                handleViewQuote,
                handlePrintQuote,
                handleDownloadQuote,
                handleSendQuote,
                handleConvertToInvoice,
                handleCloneQuote,
              }) }}
              placement="bottomLeft"
              onClick={(e) => e.stopPropagation()}
            >
              <MoreOutlined onClick={(e) => e.stopPropagation()} />
            </Dropdown>
          </Flex>
        ) : null
      }
    },
  ];

  const handleSave = (row) => {
    const newData = [...dataSource];
    const index = newData.findIndex((item) => row.key === item.key);
    const item = newData[index];
    newData.splice(index, 1, {
      ...item,
      ...row,
    });
    setDataSource(newData);
  };


  const columns = defaultColumns.map((col) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record) => ({
        record,
        editable: col.editable,
        dataIndex: col.dataIndex,
        title: col.title,
        handleSave,
      }),
    };
  });

  const [selectionType] = useState('checkbox');

  // Handle pagination change
  const handleTableChange = (page, size) => {
    if (size !== pageSize) {
      setPageSize(size);
      setCurrentPage(1); // Reset to first page when page size changes
    } else {
      setCurrentPage(page);
    }
  };


  return (
    <div style={{ padding: 10 }}>
      {contextHolder}
      {modalContextHolder}
      <div style={{ marginBottom: 8 }}>
        Showing {totalRows === 0 ? 0 : ((currentPage - 1) * pageSize) + 1}-{Math.min(currentPage * pageSize, totalRows)} of {totalRows} quotes
      </div>
      <Flex
        gap="large"
        justify="space-between"
        align="flex-start"
        wrap="wrap"
        style={{ width: "100%" }}
      >
        <div style={{ flex: '1 1 320px', minWidth: 0 }}>
          <FilterBar
            searchValue={searchTermLive}
            onSearchChange={setSearchTermLive}
            onSearch={onSearch}
            searchPlaceholder="Search quotes"
            activeCount={activeFilterCount}
            onClear={clearFilters}
            title="Filter Quotes"
            addLabel="Create Quote"
            addIcon={<UserAddOutlined />}
            onAdd={() => navigate("/quotes/new")}
            addClassName="create-quote-btn"
            onRefresh={() => fetchQuotes(currentPage, pageSize)}
            refreshing={loading}
          >
            <FilterField label="Quote Date">
              <DatePicker.RangePicker
                placeholder={['Start Date', 'End Date']}
                value={dateRange}
                onChange={onDateRangeChange}
              />
            </FilterField>
            <FilterField label="Expiry Date">
              <DatePicker.RangePicker
                placeholder={['Start Date', 'End Date']}
                value={expiryDateRange}
                onChange={onExpiryDateRangeChange}
              />
            </FilterField>
            <FilterField label="Account">
              <Select
                placeholder="Select Account"
                value={selectedAccount}
                onChange={onAccountChange}
                showSearch
                optionFilterProp="children"
                allowClear
              >
                {accounts.map((account) => (
                  <Select.Option key={account.id} value={account.id}>
                    {account.name}
                  </Select.Option>
                ))}
              </Select>
            </FilterField>
            <FilterField label="Status">
              <Select
                mode="multiple"
                placeholder="Select Status"
                value={selectedStatus}
                onChange={onStatusChange}
                allowClear
                options={quoteStatuses.map((status) => ({ value: status[0], label: status[0] }))}
              />
            </FilterField>
            <FilterField label="Product">
              <Select
                mode="multiple"
                placeholder="Select Product"
                value={selectedProducts}
                onChange={onProductsChange}
                allowClear
                optionFilterProp="label"
                options={products.map((product) => ({ value: product.id, label: product.name }))}
              />
            </FilterField>
          </FilterBar>
        </div>
        <Flex
          gap="large"
          style={{
            width: "auto",
          }}
          wrap="wrap"
        >
        </Flex>
      </Flex>
      <div style={{ marginTop: 20 }}>
        <Table
          className="quotes-table"
          rowSelection={{
            type: selectionType,
            ...rowSelection,
          }}
          loading={loading}
          onRow={(record) => ({
            onClick: () => handleRowClick(record),
          })}
          size="small"
          dataSource={dataSource}
          columns={columns}
          rowKey={row => row.id}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: totalRows,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
            pageSizeOptions: ['50', '100', '200'],
            onChange: handleTableChange,
          }}
          scroll={{
            x: 'max-content',        // Enables horizontal scroll
            y: 'calc(100vh - 350px)' // Vertical scroll
          }}
          sticky
        />
      </div>

      {/* Drawer for Quote Details */}
      <Drawer
        title={`Quote Details - ${selectedQuote?.qt_number ?? ""}`}
        placement="right"
        onClose={handleDrawerClose}
        open={drawerVisible}
        width={"50%"}
        extra={
          <PreviewDrawerActions
            onPrint={() => {
              if (pdfUrl) {
                const printWindow = window.open(pdfUrl);
                if (printWindow) {
                  printWindow.addEventListener('load', () => {
                    printWindow.print();
                  });
                }
              }
            }}
            printDisabled={!pdfUrl || pdfLoading}
            onDownload={() => previewDownloadPdf(false, true)}
            onClose={handleDrawerClose}
          />
        }
      >
        {pdfLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <Spin size="large" />
          </div>
        ) : pdfUrl && (
          <PdfViewer
            url={pdfUrl}
            title="Quote PDF"
            onDownload={() => previewDownloadPdf(false, true)}
          />
        )}
      </Drawer>

      <Drawer
        title="Send Quote"
        placement="right"
        size="large"
        onClose={() => setSendDrawerOpen(false)}
        open={sendDrawerOpen}
        width="50%"
        destroyOnClose
        extra={
          <Space>
            <Button onClick={() => setSendDrawerOpen(false)}>Cancel</Button>
          </Space>
        }
      >
        {quoteForSend ? (
          <SendQuoteDrawer instance={quoteForSend} />
        ) : (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
            <Spin size="large" />
          </div>
        )}
      </Drawer>
    </div>
  );
};
export default QuotesTable;
